/**
 * Preview sample model + token utilities
 * ──────────────────────────────────────
 * A document contract is full of binding tokens (`{{protoss:context.clientName}}`,
 * `{{protoss:item.amount}}`) and deferred blocks (contentBlock / dynamicBlock)
 * that only resolve per deal at export time. To preview a *template* we fill
 * those with a `PreviewSample` - representative mock values - or, in placeholder
 * mode, surface them as labelled chips so the author can see what is dynamic.
 *
 * This module is framework-free (no React) so it can be reused by the parser
 * library and unit-tested; the renderer turns these segments into JSX.
 */

import { FILLABLE_PREFIX, fillablePlaceholder } from '@protoss/doc';

export type PreviewMode = 'sample' | 'placeholder';

export interface PreviewSample {
  version: '1.0';
  /** Token path (after `protoss:`, e.g. `context.clientName`) → sample display value. */
  tokens: Record<string, string>;
  /** dynamicBlock dataKey → sample rows (row-scope key → cell value). */
  data: Record<string, Array<Record<string, string>>>;
  /** contentBlock dataKey (e.g. `narrative.scope_of_work`) → sample markdown. */
  narrative: Record<string, string>;
}

export function emptyPreviewSample(): PreviewSample {
  return { version: '1.0', tokens: {}, data: {}, narrative: {} };
}

/** One slice of a token-bearing string: literal text or a `{{…}}` token. */
export type TokenSegment =
  | { kind: 'text'; value: string }
  | { kind: 'token'; path: string; label: string };

const TOKEN_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

/** Drop a leading `protoss:` namespace so `{{protoss:context.x}}` → `context.x`. */
function normalizeTokenPath(raw: string): string {
  return raw.replace(/^protoss:/, '');
}

/** Split a string into literal-text and token segments (token paths normalized). */
export function splitTokens(text: string): TokenSegment[] {
  const out: TokenSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(TOKEN_RE)) {
    const start = match.index ?? 0;
    if (start > last) out.push({ kind: 'text', value: text.slice(last, start) });
    const path = normalizeTokenPath(match[1] ?? '');
    out.push({ kind: 'token', path, label: humanizeToken(path) });
    last = start + match[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', value: text.slice(last) });
  return out;
}

/** True when a string carries at least one `{{…}}` token. */
export function hasToken(text: string): boolean {
  TOKEN_RE.lastIndex = 0;
  return TOKEN_RE.test(text);
}

const STOPWORDS = new Set(['of', 'and', 'the', 'to', 'for', 'a', 'an', 'in', 'on', 'or', 'by']);

function titleCase(words: string[]): string {
  return words
    .filter(Boolean)
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (i > 0 && STOPWORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

/** Break a camelCase / snake_case / kebab identifier into lower-case words. */
function words(identifier: string): string[] {
  return identifier
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .split(/\s+/);
}

/** `context.clientName` → "Client Name"; `asset:brandLogo` → "Brand Logo". */
export function humanizeToken(path: string): string {
  const leaf = path.split(/[.:]/).pop() ?? path;
  return titleCase(words(leaf));
}

/** `scope_of_work` → "Scope of Work". */
export function humanizeSlug(slug: string): string {
  return titleCase(words(slug));
}

/**
 * Resolve a token to its sample-mode text. `item.*` tokens read the per-row
 * scope of a dynamicBlock; everything else reads `sample.tokens`, falling back
 * to a humanized label so sample mode never shows a raw `{{…}}`.
 */
export function resolveTokenText(
  path: string,
  sample: PreviewSample,
  rowScope?: Record<string, string>
): string {
  if (path.startsWith('item.') && rowScope) {
    return rowScope[path.slice('item.'.length)] ?? '';
  }
  // Unbound fillable placeholder: the user's filled value, else a visible placeholder (never a
  // humanized label - this is a fill-in slot, not a binding).
  if (path.startsWith(FILLABLE_PREFIX)) {
    const filled = sample.tokens[path];
    return typeof filled === 'string' && filled.trim()
      ? filled
      : fillablePlaceholder(path.slice(FILLABLE_PREFIX.length));
  }
  return sample.tokens[path] ?? sample.tokens[path.split(/[.:]/).pop() ?? path] ?? humanizeToken(path);
}
